import React from "react";
import ResourcesTextCard from "@/components/ui/resources-text-card";
import { IconName } from "@/lib/get-icon";
import BetterPayAndCareerIcon from "@/components/icons/better-pay-and-career-icon";
import FlexibleLearningIcon from "@/components/icons/flexible-learning-icon";
import LiveWorkshopsIcon from "@/components/icons/live-workshops-icon";
import QualityCertificationIcon from "@/components/icons/quality-certification-icon";

type Benefit = {
  iconName?: IconName;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  title: string;
  text: string;
};

// Icon name to component mapping
const iconMap: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  "flexible-learning": FlexibleLearningIcon,
  "live-workshops": LiveWorkshopsIcon,
  "quality-certification": QualityCertificationIcon,
  "better-pay-and-career": BetterPayAndCareerIcon,
};

// Helper to get icon component from either iconName or icon prop
const getIconComponent = (
  benefit: Benefit
): React.FC<React.SVGProps<SVGSVGElement>> => {
  if (benefit.iconName && iconMap[benefit.iconName]) {
    return iconMap[benefit.iconName];
  }
  return benefit.icon!;
};

export interface BenefitsCIIONProps {
  id?: string;
  title?: string;
  benefits?: Benefit[];
}

export const defaultBenefitsCIION: Benefit[] = [
  {
    iconName: "flexible-learning" as IconName,
    title: "Flexible Learning",
    text: "Learn on your time, on any internet capable device, wherever you may be.",
  },
  {
    iconName: "live-workshops" as IconName,
    title: "Live Workshops",
    text: "Professionals and healthcare providers are brought together to speak about new trends in the healthcare community and industry.",
  },
  {
    iconName: "quality-certification" as IconName,
    title: "Quality Certification",
    text: "We work with the highest state and industry standards to ensure quality certification and CEUs (when applicable).",
  },
  {
    iconName: "better-pay-and-career" as IconName,
    title: "Better Pay & Career",
    text: "With better education and certification, you can earn more money than an uneducated or uncertified assistant.",
  },
];

const BenefitsCIION: React.FC<BenefitsCIIONProps> = ({
  id = "benefits-ci-ion",
  benefits = defaultBenefitsCIION,
  title = "Benefits",
}) => {
  return (
    <section
      id={id}
      className="bg-white"
      aria-labelledby="benefits-heading"
    >
      <div className="mx-auto grid max-w-lg grid-cols-1 gap-4 p-4 md:gap-6 md:px-6 md:py-8 lg:gap-8 lg:px-3 lg:py-10">
        <h2
          id="benefits-heading"
          className="text-center font-montserrat text-heading-4 text-neutral-600 md:text-heading-3 lg:text-start lg:text-heading-2"
        >
          {title}
        </h2>
        <div
          className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-4"
          role="list"
        >
          {benefits.map((benefit, index) => (
            <ResourcesTextCard
              key={benefit.title}
              cardType="icon-feature-ion"
              icon={getIconComponent(benefit)}
              title={benefit.title}
              text={benefit.text}
              aria-labelledby={`benefit-title-${index}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default BenefitsCIION;
